import React, { useEffect, useMemo, useState } from 'react';
import { CheckIcon, MinusIcon, PlusIcon, SearchIcon, XIcon } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { activityDefinitions, activityById } from '../../data/activityCatalog';
import { contactTypeMeta, contactsForActivity, preferredContactTypes } from '../../data/contacts';
import { useActivityPlan } from '../../contexts/ActivityPlanContext';
import { activityIconClasses } from '../../utils/activityStyles';
import type { ActivityCategory, ActivityContact, LogChannel } from '../../types/activity';
interface LogActivityModalProps {
  open: boolean;
  onClose: () => void;
  defaultCategory?: ActivityCategory;
  lockCategory?: boolean;
  /** Pre-attaches the activity to a realtor, prospect, lead, or past client. */
  contact?: ActivityContact;
  lockContact?: boolean;
}
const channels: {
  id: LogChannel;
  label: string;
}[] = [{
  id: 'call',
  label: 'Call'
}, {
  id: 'text',
  label: 'Text'
}, {
  id: 'email',
  label: 'Email'
}, {
  id: 'social',
  label: 'Social'
}, {
  id: 'in-person',
  label: 'In person'
}, {
  id: 'video',
  label: 'Video'
}];
export function LogActivityModal({
  open,
  onClose,
  defaultCategory = 'realtor-outreach',
  lockCategory = false,
  contact,
  lockContact = false
}: LogActivityModalProps) {
  const {
    logActivity,
    logs
  } = useActivityPlan();
  const [category, setCategory] = useState<ActivityCategory>(defaultCategory);
  const [selected, setSelected] = useState<ActivityContact | null>(contact ?? null);
  const [search, setSearch] = useState('');
  const [count, setCount] = useState(1);
  const [channel, setChannel] = useState<LogChannel>('call');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setCategory(defaultCategory);
    setSelected(contact ?? null);
    setSearch('');
    setCount(1);
    setChannel('call');
    setNote('');
    setSaving(false);
  }, [open, defaultCategory, contact]);
  const definition = activityById[category];
  const options = lockCategory ? activityDefinitions.filter(item => item.id === defaultCategory) : activityDefinitions;
  const recentContacts = useMemo(() => {
    const seen = new Map<string, ActivityContact>();
    logs.forEach(log => {
      if (log.contact && !seen.has(log.contact.id)) {
        seen.set(log.contact.id, log.contact);
      }
    });
    return Array.from(seen.values()).filter(item => preferredContactTypes[category].includes(item.type)).slice(0, 4);
  }, [logs, category]);
  const matches = useMemo(() => {
    const term = search.trim().toLowerCase();
    const pool = contactsForActivity(category);
    if (!term) return pool.slice(0, 6);
    return pool.filter(item => item.name.toLowerCase().includes(term) || item.detail.toLowerCase().includes(term)).slice(0, 8);
  }, [search, category]);
  const contactRequired = definition.requiresContact;
  const handleSave = () => {
    if (contactRequired && !selected) return;
    setSaving(true);
    window.setTimeout(() => {
      logActivity({
        category,
        count,
        contact: selected ?? undefined,
        channel,
        note: note.trim() || undefined
      });
      setSaving(false);
      onClose();
    }, 300);
  };
  return <Modal open={open} onClose={onClose} title="Log activity" description={contactRequired ? 'This activity is credited to a realtor, prospect, lead, or past client.' : 'Social engagement can be logged as volume — tagging a contact is optional.'} footer={<div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between" data-id="element-80" data-mp="70fonvaqmcyu">
          <p className="text-xs text-navy-500" data-id="element-81" data-mp="21c49uf7kbbro">
            {selected ? `Crediting ${selected.name}` : contactRequired ? 'Pick who this activity was with to continue' : 'No contact attached'}
          </p>
          <div className="flex flex-col-reverse gap-2 sm:flex-row" data-id="element-82" data-mp="68fri2ltcklk">
            <button type="button" onClick={onClose} className="inline-flex h-11 items-center justify-center rounded-lg border border-navy-200 px-4 text-sm font-semibold text-navy-700 hover:bg-navy-50" data-id="element-83" data-mp="19mionhh1hrq">
              Cancel
            </button>
            <button type="button" onClick={handleSave} disabled={saving || contactRequired && !selected} className="inline-flex h-11 items-center justify-center rounded-lg bg-brand-600 px-4 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50" data-id="element-84" data-mp="3jfraj9xtg4pp">
              {saving ? 'Saving…' : `Log ${count} ${definition.unit}`}
            </button>
          </div>
        </div>} data-id="element-79" data-mp="uu7f6iyv80vl">
      <div className="flex flex-col gap-5" data-id="element-85" data-mp="2vpwteg6grtzn">
        <fieldset data-id="element-86" data-mp="2vr7z4ljgzbte">
          <legend className="text-xs font-semibold uppercase tracking-wide text-navy-500" data-id="element-87" data-mp="2vqoqjvfplzs1">
            Activity
          </legend>
          <div className="mt-2 flex flex-wrap gap-2" data-id="element-88" data-mp="1g6yxvajnzf1l">
            {options.map(item => {
            const active = item.id === category;
            const Icon = item.icon;
            return <button key={item.id} type="button" aria-pressed={active} onClick={() => setCategory(item.id)} className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold ${active ? 'border-brand-300 bg-brand-50 text-brand-700' : 'border-navy-200 text-navy-700 hover:bg-navy-50'}`} data-id="element-89" data-mp="1p7dn2kzr1ead">
                  <span className={`flex h-6 w-6 items-center justify-center rounded-md ${activityIconClasses[item.tone]}`} data-id="element-90" data-mp="9resydmyvit4">
                    <Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-91" data-mp="dqdy9zcm1hl3" />
                  </span>
                  {item.shortLabel}
                </button>;
          })}
          </div>
        </fieldset>

        <div data-id="element-92" data-mp="1qt7glz2hmjx7">
          <p className="text-xs font-semibold uppercase tracking-wide text-navy-500" data-id="element-93" data-mp="1qtpcgul8mw4l">
            Who was this with?{' '}
            {contactRequired ? <span className="text-danger-600" data-id="element-94" data-mp="1lw7cvut41t99">Required</span> : <span className="text-navy-400" data-id="element-95" data-mp="y51w9dfciwxw">Optional</span>}
          </p>

          {selected ? <div className="mt-2 flex items-center justify-between gap-3 rounded-xl border border-brand-200 bg-brand-50 px-3 py-2.5" data-id="element-96" data-mp="t79xbzgaaak7">
              <div className="min-w-0" data-id="element-97" data-mp="133uyg9p6n1ir">
                <div className="flex flex-wrap items-center gap-2" data-id="element-98" data-mp="132tqie3gqv59">
                  <p className="text-sm font-semibold text-navy-900" data-id="element-99" data-mp="2iju2b8rrobd7">
                    {selected.name}
                  </p>
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ring-1 ${contactTypeMeta[selected.type].classes}`} data-id="element-100" data-mp="2ei07hh4961ky">
                    {contactTypeMeta[selected.type].label}
                  </span>
                </div>
                <p className="truncate text-xs text-navy-600" data-id="element-101" data-mp="2c67swhu7z490">
                  {selected.detail}
                </p>
              </div>
              {lockContact ? null : <button type="button" onClick={() => setSelected(null)} className="inline-flex h-9 items-center gap-1 rounded-lg border border-navy-200 bg-white px-2.5 text-xs font-semibold text-navy-600 hover:bg-navy-50" data-id="element-102" data-mp="34taob13lmyv1">
                  <XIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-103" data-mp="300r77u5pdllh" />
                  Change
                </button>}
            </div> : <div className="mt-2" data-id="element-104" data-mp="3siwn3ng27c2i">
              {recentContacts.length > 0 ? <div className="mb-2 flex flex-wrap gap-2" data-id="element-105" data-mp="1sfve0f0bitoj">
                  {recentContacts.map(item => <button key={item.id} type="button" onClick={() => setSelected(item)} className="inline-flex h-9 items-center rounded-full border border-navy-200 px-3 text-xs font-semibold text-navy-700 hover:bg-navy-50" data-id="element-106" data-mp="1novgb7jdk7kz">
                      {item.name}
                    </button>)}
                </div> : null}

              <div className="relative" data-id="element-107" data-mp="27ba1l76helze">
                <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-navy-400" aria-hidden="true" data-id="element-108" data-mp="22di848q6urt3" />
                <input type="search" value={search} onChange={event => setSearch(event.target.value)} aria-label="Search realtors, prospects, leads, and past clients" placeholder="Search realtors, prospects, leads, past clients" className="h-11 w-full rounded-lg border border-navy-200 pl-9 pr-3 text-sm text-navy-900 placeholder:text-navy-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100" data-id="element-109" data-mp="qznxi7a54ckr" />
              </div>

              <ul className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-navy-100" data-id="element-110" data-mp="qmtwdk87jcog">
                {matches.length === 0 ? <li className="px-3 py-4 text-center text-sm text-navy-500" data-id="element-111" data-mp="zkw4sku9t7lr">
                    No matching contacts. Try another name.
                  </li> : matches.map(item => <li key={item.id} data-id="element-112" data-mp="19jd4c679yujf">
                      <button type="button" onClick={() => setSelected(item)} className="flex w-full items-center justify-between gap-3 border-b border-navy-50 px-3 py-2.5 text-left last:border-b-0 hover:bg-navy-50" data-id="element-113" data-mp="36c56op6iq6nv">
                        <span className="min-w-0" data-id="element-114" data-mp="bo39flwkz3o0">
                          <span className="block truncate text-sm font-semibold text-navy-900" data-id="element-115" data-mp="bov6l15tt9ge">
                            {item.name}
                          </span>
                          <span className="block truncate text-xs text-navy-500" data-id="element-116" data-mp="kqci068agmyr">
                            {item.detail}
                          </span>
                        </span>
                        <span className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ring-1 ${contactTypeMeta[item.type].classes}`} data-id="element-117" data-mp="umnx13z9jqww">
                          {contactTypeMeta[item.type].label}
                        </span>
                      </button>
                    </li>)}
              </ul>
            </div>}
        </div>

        <div data-id="element-118" data-mp="1nif1u8ywrapy">
          <p className="text-xs font-semibold uppercase tracking-wide text-navy-500" data-id="element-119" data-mp="1ni5jn7k3u7ut">
            How many {definition.unit}?
          </p>
          <div className="mt-2 flex items-center gap-3" data-id="element-120" data-mp="1ikvodba1b1z2">
            <button type="button" onClick={() => setCount(value => Math.max(1, value - 1))} aria-label={`Decrease ${definition.unit}`} className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-navy-200 text-navy-700 hover:bg-navy-50" data-id="element-121" data-mp="1diy0u4ulpl0b">
              <MinusIcon className="h-4 w-4" aria-hidden="true" data-id="element-122" data-mp="21a2913ue23ni" />
            </button>
            <span className="w-12 text-center text-2xl font-bold tabular-nums text-navy-900" aria-live="polite" data-id="element-123" data-mp="1wh0rsp6pls0r">
              {count}
            </span>
            <button type="button" onClick={() => setCount(value => Math.min(50, value + 1))} aria-label={`Increase ${definition.unit}`} className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-navy-200 text-navy-700 hover:bg-navy-50" data-id="element-124" data-mp="2ozo5vknag6sc">
              <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-125" data-mp="nbjo359co3cz" />
            </button>
          </div>
        </div>

        <fieldset data-id="element-126" data-mp="djmchxefj9dq">
          <legend className="text-xs font-semibold uppercase tracking-wide text-navy-500" data-id="element-127" data-mp="dj47ocdpbmu7">
            Channel
          </legend>
          <div className="mt-2 flex flex-wrap gap-2" data-id="element-128" data-mp="3vnxpiha0c5sm">
            {channels.map(item => {
            const active = item.id === channel;
            return <button key={item.id} type="button" aria-pressed={active} onClick={() => setChannel(item.id)} className={`inline-flex h-9 items-center gap-1 rounded-full border px-3 text-sm font-semibold ${active ? 'border-navy-800 bg-navy-800 text-white' : 'border-navy-200 text-navy-600 hover:bg-navy-50'}`} data-id="element-129" data-mp="3ls4gh7vhyufl">
                  {active ? <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-130" data-mp="34jlapml46qwm" /> : null}
                  {item.label}
                </button>;
          })}
          </div>
        </fieldset>

        <div data-id="element-131" data-mp="3ee3mlf0opsri">
          <label htmlFor="activity-note" className="text-xs font-semibold uppercase tracking-wide text-navy-500" data-id="element-132" data-mp="3s7l1gmifehb7">
            Note (optional)
          </label>
          <textarea id="activity-note" value={note} onChange={event => setNote(event.target.value)} rows={2} placeholder="Anything your coach should see" className="mt-2 w-full rounded-lg border border-navy-200 px-3 py-2 text-sm text-navy-900 placeholder:text-navy-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100" data-id="element-133" data-mp="60d2q320h8o5" />
        </div>
      </div>
    </Modal>;
}